import { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const userIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

const storeIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

const selectedStoreIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

function FlyTo({ lat, lng, zoom = 14 }) {
    const map = useMap();
    useEffect(() => {
        if (lat == null || lng == null || Number.isNaN(lat) || Number.isNaN(lng)) return;
        map.flyTo([lat, lng], zoom, { duration: 0.8 });
    }, [lat, lng, zoom, map]);
    return null;
}

export default function StoreLocatorMap({
    stores,
    userLocation,
    selectedStoreId,
    onSelectStore,
    height = 350,
}) {
    const defaultCenter = [-1.286389, 36.817223];

    const selectedStore = useMemo(
        () =>
            (stores || []).find(
                (s) => String(s._id || '') === String(selectedStoreId || ''),
            ),
        [stores, selectedStoreId],
    );

    const selectedLat = selectedStore?.location?.latitude;
    const selectedLng = selectedStore?.location?.longitude;
    const hasSelectedCoords =
        selectedLat != null &&
        selectedLng != null &&
        !Number.isNaN(Number(selectedLat)) &&
        !Number.isNaN(Number(selectedLng));

    // Prefer selected store for map focus; fall back to user / Nairobi
    const focusLat = hasSelectedCoords
        ? Number(selectedLat)
        : userLocation?.lat != null
          ? Number(userLocation.lat)
          : defaultCenter[0];
    const focusLng = hasSelectedCoords
        ? Number(selectedLng)
        : userLocation?.lng != null
          ? Number(userLocation.lng)
          : defaultCenter[1];

    const initialCenter = [focusLat, focusLng];

    return (
        <div style={{ height: `${height}px`, width: '100%', borderRadius: '12px', overflow: 'hidden', border: '1px solid #E0E0E0' }}>
            <MapContainer center={initialCenter} zoom={hasSelectedCoords ? 14 : 12} style={{ height: '100%', width: '100%' }}>
                <FlyTo lat={focusLat} lng={focusLng} zoom={hasSelectedCoords ? 15 : 12} />
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {userLocation?.lat != null && userLocation?.lng != null && (
                    <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
                        <Popup>Your location</Popup>
                    </Marker>
                )}

                {(stores || []).map((store) => {
                    const lat = store.location?.latitude;
                    const lng = store.location?.longitude;
                    if (lat == null || lng == null) return null;
                    const isSelected = String(store._id || '') === String(selectedStoreId || '');
                    return (
                        <Marker
                            key={store._id}
                            position={[lat, lng]}
                            icon={isSelected ? selectedStoreIcon : storeIcon}
                            eventHandlers={{ click: () => onSelectStore?.(store) }}
                        >
                            <Popup>
                                <strong>{store.storeName}</strong>
                                {store.address && <><br />{store.address}</>}
                                {Number.isFinite(store.distanceKm) && store.distanceKm < Infinity && (
                                    <><br /><span className="text-muted">{Number(store.distanceKm).toFixed(1)} km away</span></>
                                )}
                                {onSelectStore && (
                                    <>
                                        <br />
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary mt-1"
                                            onClick={() => onSelectStore(store)}
                                        >
                                            Select this store
                                        </button>
                                    </>
                                )}
                            </Popup>
                        </Marker>
                    );
                })}
            </MapContainer>
        </div>
    );
}
